import React from 'react'

import { Box, Typography } from '@mui/material'
import { MessageCircle } from 'lucide-react'

import TrackingDetails from '../../../../shared/components/TrackingDetails/TrackingDetails.component'

import date from '../../../../shared/functions/Date/date.functions'
import { stripPrefixFromId } from '../../../../shared/functions/ID/id.functions'
import styles from './TrackingHero.styles'
import type { TrackingHeroProps } from './TrackingHero.types'

const formatOccurredAt = (occurredAt: string | null): string | null => {
  if (!occurredAt) return null

  return date(occurredAt).format('MMM d, h:mm a') ?? null
}

const buildMailtoUrl = (supportEmail: string | null, orderId: string): string => {
  if (!supportEmail) return '#'
  const subject = encodeURIComponent(`Fulfillment Order Support - Order: ${stripPrefixFromId(orderId)}`)

  return `mailto:${supportEmail}?subject=${subject}`
}

const TrackingHero: React.FC<TrackingHeroProps> = (props) => {
  const { orderId, estimatedDeliveryAt, steps, supportEmail } = props

  const subtitleParts: string[] = [`Order: ${stripPrefixFromId(orderId)}`]
  const mailtoUrl = buildMailtoUrl(supportEmail, orderId)
  const isSupportAvailable = Boolean(supportEmail)
  const SupportCtaWrapper = isSupportAvailable ? ('a' as const) : ('div' as const)
  const supportCtaProps = isSupportAvailable ? { href: mailtoUrl } : {}

  const stepperSteps = steps.map((step) => ({
    label: step.label,
    subLabel: formatOccurredAt(step.occurredAt) ?? step.description ?? 'Pending'
  }))

  const currentIndex = steps.findIndex((step) => step.state === 'current')
  const activeStep =
    currentIndex >= 0 ? currentIndex : steps[steps.length - 1]?.state === 'completed' ? steps.length - 1 : 0
  const isLastStepCompleted = steps[steps.length - 1]?.state === 'completed'

  if (estimatedDeliveryAt) {
    const formatted = date(estimatedDeliveryAt).format('MMM d, yyyy')

    if (formatted) {
      subtitleParts.push(`Estimated delivery: ${formatted}`)
    }
  }

  const heroSubtitle = subtitleParts.join(' · ')

  return (
    <Box sx={styles.hero}>
      <Typography sx={styles.heroLabel}>Order Tracking</Typography>

      <Box sx={styles.titleRow}>
        <Typography sx={styles.heroTitle}>Your order is on its way</Typography>

        <Box sx={styles.desktopSupportGroup}>
          <Typography sx={styles.needHelp}>Need help?</Typography>

          <Box component={SupportCtaWrapper} sx={styles.supportCta} {...supportCtaProps}>
            <MessageCircle size={14} color="#FFFFFF" />
            <Typography sx={styles.supportCtaText}>Contact Support</Typography>
          </Box>
        </Box>
      </Box>

      <Typography sx={styles.heroSub}>{heroSubtitle}</Typography>

      <Box sx={styles.mobileSupportRow}>
        <Typography sx={styles.mobileSupportNeedHelp}>Need help?</Typography>

        <Box component={SupportCtaWrapper} sx={styles.mobileSupportCta} {...supportCtaProps}>
          <MessageCircle size={12} color="#FFFFFF" />
          <Typography sx={styles.mobileSupportCtaText}>Contact Support</Typography>
        </Box>
      </Box>

      {steps.length > 0 && (
        <Box sx={styles.trackingDetailsContainer}>
          <TrackingDetails
            isOpen
            steps={stepperSteps}
            activeStep={activeStep}
            isLastStepCompleted={isLastStepCompleted}
            squareBottomCorners
          />
        </Box>
      )}
    </Box>
  )
}

export default TrackingHero
